import { Done } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { ETimeSyncStatus, type ICompanyTimeSyncStatis } from '@sharedInterfaces/ICompanySettings';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import setZepSync from '@src/APIs/graphQl/Company/setZepSync';
import Button from '@src/App/Components/Buttons/Button/Button';
import ErrorBox from '@src/App/Components/Error/ErrorBox/ErrorBox';
import TextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/TextInput/TextInput';
import RowElement from '@src/App/Components/General/FormularElements/RowElement/RowElement';
import type Company from '@src/Objects/Company';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { StatusTable } from './ClockodoAccordion';

interface ZepAccordionProps {
    active: boolean;
    statis: ICompanyTimeSyncStatis;
    zepInstance: string;
    setCompany: (company: Company) => void;
    reload: () => void;
}
export function ZepAccordion({ active, statis, zepInstance, setCompany, reload }: ZepAccordionProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const [changed, setChanged] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<string | false>(active ? 'zep' : false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [token, setToken] = useState<string>('');
    const [instance, setInstance] = useState<string>(zepInstance);

    const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };
    return (
        <Accordion expanded={active || expanded === 'zep'} disabled={saving} onChange={handleChange('zep')}>
            <AccordionSummary>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                        style={{
                            backgroundImage: `url(/icons/zep-50.png)`,
                            width: '30px',
                            height: '30px',
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                        }}
                    />
                    <Typography fontWeight={active ? 'bold' : 'normal'}>{langStrings.zep}</Typography>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <RowElement alignTitle="left" title={langStrings.zepInstance}>
                    <TextInput
                        value={instance}
                        size="small"
                        onChange={(text) => {
                            setChanged(true);
                            setInstance(text);
                        }}
                    />
                </RowElement>
                <RowElement alignTitle="left" title={langStrings.zepToken}>
                    <TextInput
                        value={token}
                        password={true}
                        size="small"
                        onChange={(text) => {
                            setChanged(true);
                            setToken(text);
                        }}
                    />
                </RowElement>
                {saveError && <ErrorBox close={setSaveError.bind(null, null)}>{saveError}</ErrorBox>}

                {changed && (
                    <Button
                        disabled={saving || token === '' || token.length !== 64}
                        size={'normal'}
                        text={langStrings.save}
                        icon={<Done />}
                        color="primary"
                        onClick={(): void => {
                            setSaving(true);
                            setZepSync({
                                instance,
                                token,
                            })
                                .then((companyData) => {
                                    const error = companyData.zepStats.status === ETimeSyncStatus.ERROR; //TODO: CHANGE
                                    console.log('companyData', companyData.zepStats.status);

                                    setCompany(companyData);
                                    if (error) {
                                        setSaveError(langStrings.errorLoginData);
                                    } else {
                                        setSaveError(null);
                                    }
                                })
                                .then(() => setChanged(false))
                                .catch((err) => setSaveError(err.toString()))
                                .finally(() => {
                                    setSaving(false);
                                    setTimeout(reload, 2000);
                                });
                        }}
                    />
                )}
                <StatusTable statis={statis} syncIsActive={active} reload={reload} />
            </AccordionDetails>
        </Accordion>
    );
}
